/**
 * The levelsStore toggles listed in the Levels panel, with their labels and
 * tooltip texts, plus which tab each one lives on.
 */
import type { LevelType } from '../../store/levelsStore';
import type { LevelsTab } from '../../store/levelsPanelUiStore';

/** levelsStore toggles shown in this panel: the order-flow ones live in the TradeFinder panel. */
export type PanelLevelType = Exclude<LevelType, `flow${string}`>;

export interface LevelConfig {
  key: PanelLevelType;
  label: string;
  description: string;
}

export const LEVEL_CONFIGS: LevelConfig[] = [
  { key: 'sessionHodLod', label: 'Session HOD/LOD', description: 'Full session high/low (5:02 PM - 5:02 PM CT)' },
  { key: 'rthHodLod', label: 'RTH HOD/LOD', description: 'Regular Trading Hours (8:30 AM - 3:15 PM CT)' },
  { key: 'prevDayHodLod', label: 'PDH/PDL', description: 'Previous Day High/Low' },
  { key: 'ibRange', label: 'IB Range', description: 'Initial Balance (8:30-9:30 AM CT)' },
  { key: 'onhOnl', label: 'ONH/ONL', description: 'Overnight high/low, 5:00 PM - 8:30 AM CT' },
  { key: 'midnightOpen', label: 'Midnight Open', description: 'Midnight ET (11 PM CT)' },
  { key: 'mocOpen', label: 'MOC', description: 'MOC imbalance - open of the 12:50 PM PT candle (2:50 PM CT)' },
  { key: 'prevWeekHodLod', label: 'Prev Week H/L', description: 'Previous CME Week High/Low (Sun 5PM - Fri 4PM CT)' },
  { key: 'currWeekHodLod', label: 'Curr Week H/L', description: 'Current CME Week High/Low' },
  { key: 'prevMonthHodLod', label: 'Prev Month H/L', description: 'Previous Calendar Month High/Low' },
  { key: 'fibonacci', label: 'Fibonacci', description: 'Fibonacci Retracement (Session HOD to LOD)' },
  { key: 'donchian', label: 'Donchian', description: 'Donchian Channels (20 + 55 bar breakout)' },
  { key: 'ema9', label: 'EMA 9', description: '9-period exponential moving average' },
  { key: 'ema21', label: 'EMA 21', description: '21-period exponential moving average' },
  { key: 'ema200', label: 'EMA 200', description: '200-period exponential moving average' },
  { key: 'vwap', label: 'VWAP', description: 'Session Volume-Weighted Average Price' },
  {
    key: 'vestVolume',
    label: 'Vest NQ volume',
    description:
      'Vest NQ-PERP volume as thin amber bars inside the CME volume pane (MNQ/NQ only). Light = taker buys > sells, dark hatched = sells > buys, outline = kline-derived',
  },
  {
    key: 'cmeVolSpikes',
    label: 'CME volume spikes',
    description: 'Unusual CME volume in the volume pane: the bar drawn brighter with its multiple ("2.4x") above it',
  },
  {
    key: 'vestVolSpikes',
    label: 'Vest volume spikes 🐋',
    description: 'Unusual Vest NQ-PERP volume in the volume pane: a small 🐋 above the thin Vest bar (same rule as the candle 🐋)',
  },
  {
    key: 'vestSpikes',
    label: 'Vest spikes 🐋 on candles',
    description:
      '🐋 on a closed candle whose Vest NQ-PERP volume is at least the threshold times its baseline (auto: same time of day over the last 10 sessions, else the previous N bars; same rule as the CME 🦈) (MNQ/NQ only). Above the high on net taker buying, below the low on net selling',
  },
  {
    key: 'cmeSpikes',
    label: 'CME spikes 🦈 on candles',
    description: '🦈 on a closed candle with unusual CME volume (same rule as the volume-pane CME marker), on the side of its CME delta',
  },
  {
    key: 'bothSpikes',
    label: 'Both agree 🐋🦈',
    description:
      '🐋🦈 when CME and Vest both spiked on the same closed bar AND their deltas agree: above on net buying, below on net selling. Shows even with the single toggles off',
  },
  {
    key: 'vestCat',
    label: 'Vest bar above CME 🐱',
    description:
      '🐱 on a closed candle whose thin Vest bar visibly sticks out above its CME bar in the volume pane, as the pane is currently scaled (MNQ/NQ only; needs the Vest bars on). Hover shows the raw Vest units vs CME contracts. Above on Vest net buying, below on net selling. Display only, no alerts',
  },
  {
    key: 'wickRejection',
    label: 'Wick rejection 🪝',
    description:
      '🪝 at the tip of a long rejection wick on a closed candle: possible reversal. Faint until a later bar closes past the body, gone if the tip trades first. Larger with an amber rim when the wick swept a level (HOD/LOD, PDH/PDL, ONH/ONL, VWAP), a 🐋/🦈 spike hit the bar, or CME delta absorbed into it. Display only, no alerts',
  },
  {
    key: 'vestAtrSizing',
    label: 'ATR sizing (Vest)',
    description:
      'Vest charts only (BTC-PERP.VEST ...): faint dotted suggested SL / TP lines from the service\'s ATR (both sides of the price when flat, from the entry on an open position) and a legend with ATR, SL, TP and the size for your risk $. Suggestions only, no orders. Off = no lines, no legend, no requests',
  },
];

/** Tab for every LEVEL_CONFIGS entry (absorption lives on Markers, outside LEVEL_CONFIGS). */
export const LEVEL_TAB: Record<PanelLevelType, LevelsTab> = {
  sessionHodLod: 'price',
  rthHodLod: 'price',
  prevDayHodLod: 'price',
  ibRange: 'price',
  onhOnl: 'price',
  midnightOpen: 'price',
  mocOpen: 'price',
  prevWeekHodLod: 'price',
  currWeekHodLod: 'price',
  prevMonthHodLod: 'price',
  ema9: 'indicators',
  ema21: 'indicators',
  ema200: 'indicators',
  vwap: 'indicators',
  fibonacci: 'indicators',
  donchian: 'indicators',
  vestVolume: 'volume',
  cmeVolSpikes: 'volume',
  vestVolSpikes: 'volume',
  vestSpikes: 'volume',
  cmeSpikes: 'volume',
  bothSpikes: 'volume',
  vestCat: 'volume',
  wickRejection: 'markers',
  vestAtrSizing: 'price',
  absorption: 'markers',
};
